import { useState, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { toastOptions } from "../../../assets/constants";
import { getEntryId, normalizeDuration, TIMEZONE } from "../utils/trackingUtils";

const useEntryOperations = ({ entries, setEntries, token, refreshDashboard, activeDay }) => {
	const [submittingEntries, setSubmittingEntries] = useState(new Set());

	const handleEditEntry = useCallback(
		(id, updatedEntry) => {
			setEntries((prev) => prev.map((entry) => (getEntryId(entry) === id ? updatedEntry : entry)));
		},
		[setEntries]
	);

	const handleActivateEditing = useCallback(
		(id) => {
			setEntries((prev) =>
				prev.map((entry) =>
					entry.id === id
						? { ...entry, isEditing: true, originalEntry: { ...entry } }
						: entry.originalEntry
						? { ...entry.originalEntry, isEditing: false }
						: { ...entry, isEditing: false }
				)
			);
		},
		[setEntries]
	);

	const handleCancelEdit = useCallback(
		(id) => {
			setEntries((prev) =>
				prev.map((entry) => {
					if (getEntryId(entry) !== id) return entry;
					if (entry.originalEntry) {
						return { ...entry.originalEntry, isEditing: false };
					}
					return { ...entry, isEditing: false };
				})
			);
		},
		[setEntries]
	);

	const handleDeleteEntry = useCallback(
		(entry) => {
			if (entry.tempId) {
				setEntries((prev) => prev.filter((e) => e.tempId !== entry.tempId));
				return;
			}

			axios
				.delete(`${process.env.REACT_APP_UPA_API_HOST}tracking/entries/${entry.id}`, {
					headers: { Authorization: `Bearer ${token}` },
				})
				.then(() => {
					setEntries((prev) => prev.filter((e) => e.id !== entry.id));
					refreshDashboard();
				})
				.catch(() => {
					toast.error("Une erreur est survenue", toastOptions);
				});
		},
		[setEntries, token, refreshDashboard]
	);

	const handleDuplicateEntry = useCallback(
		(entry) => {
			const newTempId = Date.now();
			setEntries((prev) => {
				const maxOrder = Math.max(...prev.map((e) => e.customOrder || 0), 0);
				const duplicatedEntry = {
					tempId: newTempId,
					date: activeDay.tz(TIMEZONE).format("YYYY-MM-DD"),
					projectId: entry.task?.section?.projectId,
					taskId: entry.task?.id,
					comments: "",
					duration: entry.duration,
					customOrder: maxOrder + 1,
					isEditing: true,
					originalEntry: null,
				};
				return [...prev, duplicatedEntry];
			});
		},
		[setEntries, activeDay]
	);

	const handleSubmitEntry = useCallback(
		(entry) => {
			const entryKey = getEntryId(entry);

			if (submittingEntries.has(entryKey)) return;

			const durationValue = normalizeDuration(entry.duration);

			if (!entry.projectId || !entry.taskId || durationValue <= 0) {
				toast.error("Merci de remplir tous les champs", toastOptions);
				return;
			}

			setSubmittingEntries((prev) => new Set(prev).add(entryKey));

			const payload = { ...entry, duration: durationValue };
			const isNew = !!entry.tempId;
			const url = isNew
				? `${process.env.REACT_APP_UPA_API_HOST}tracking/entries`
				: `${process.env.REACT_APP_UPA_API_HOST}tracking/entries/${entry.id}`;
			const method = isNew ? "post" : "put";

			axios[method](url, payload, { headers: { Authorization: `Bearer ${token}` } })
				.then((response) => {
					const updatedEntry = { ...response.data, projectId: response.data.task.section.projectId };
					setEntries((prev) =>
						prev.map((e) => {
							if (isNew && e.tempId === entry.tempId) return updatedEntry;
							if (!isNew && e.id === entry.id) return updatedEntry;
							return e;
						})
					);
					refreshDashboard();
				})
				.catch(() => {
					toast.error("Une erreur est survenue", toastOptions);
				})
				.finally(() => {
					setSubmittingEntries((prev) => {
						const newSet = new Set(prev);
						newSet.delete(entryKey);
						return newSet;
					});
				});
		},
		[setEntries, token, refreshDashboard, submittingEntries]
	);

	return {
		submittingEntries,
		handleEditEntry,
		handleActivateEditing,
		handleCancelEdit,
		handleDeleteEntry,
		handleDuplicateEntry,
		handleSubmitEntry,
	};
};

export default useEntryOperations;
